import { invokeApi, API_URL } from '@/lib/apiClient';
import { supabase } from '@/integrations/supabase/client';

// API_URL is now imported from @/lib/apiClient
const MAX_EXTRACTED_TEXT_LENGTH = 300000;

export interface UploadedFile {
  name: string;
  size: number;
  type: string;
  url: string;
  path: string;
  extractedText: string;
}

function truncateExtractedText(text: string, maxLength = MAX_EXTRACTED_TEXT_LENGTH): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '\n\n[... texto truncado por longitud ...]';
}

/**
 * Get a valid access token, proactively refreshing if it expires within 60 seconds.
 */
async function getValidAccessToken(): Promise<string | null> {
  let { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;

  // Check if token is about to expire (within 60 seconds)
  const expiresAt = session.expires_at;
  const now = Math.floor(Date.now() / 1000);
  const isExpiringSoon = expiresAt && (expiresAt - now) < 60;

  if (isExpiringSoon) {
    console.log('[fileUpload] Token expiring soon, refreshing...');
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshData.session) {
      console.error('[fileUpload] Failed to refresh session:', refreshError);
      return session.access_token; // Return old token as fallback
    }
    session = refreshData.session;
    console.log('[fileUpload] Token refreshed successfully');
  }

  return session.access_token;
}

/**
 * Sends a file to the VPS to extract its text without freezing the browser.
 * Falls back to the extract-document edge function if VPS fails.
 */
async function extractTextViaVPS(file: File, anonymize: boolean = false): Promise<string> {
  // ── Attempt 1: VPS API ──
  try {
    let token = await getValidAccessToken();
    const formData = new FormData();
    formData.append('file', file);
    if (anonymize) formData.append('anonymize', 'true');

    let res = await fetch(`${API_URL}/upload-document`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });

    // Retry once on 401 with a refreshed token
    if (res.status === 401) {
      console.log('[fileUpload] 401 on upload-document, refreshing token and retrying...');
      const { data: refreshData } = await supabase.auth.refreshSession();
      if (refreshData.session) {
        token = refreshData.session.access_token;
        const retryFormData = new FormData();
        retryFormData.append('file', file);
        if (anonymize) retryFormData.append('anonymize', 'true');
        res = await fetch(`${API_URL}/upload-document`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: retryFormData,
        });
      }
    }

    if (res.ok) {
      const data = await res.json();
      const text = data.extractedText || '';
      // If VPS returned actual text (not the fallback message), use it
      if (text && text !== '[No se pudo extraer el texto]') {
        return text;
      }
      console.warn('[fileUpload] VPS returned empty/fallback text, trying edge function...');
    } else {
      console.warn(`[fileUpload] VPS error ${res.status}, trying edge function...`);
    }
  } catch (error) {
    console.warn('[fileUpload] VPS unreachable, trying edge function...', error);
  }

  // ── Attempt 2: Edge Function (Gemini-based extraction) ──
  try {
    console.log('[fileUpload] Falling back to extract-document edge function');
    const fallbackFormData = new FormData();
    fallbackFormData.append('file', file);

    const token = await getValidAccessToken();
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://qclsoabgxbmtaolyzmfz.supabase.co';
    const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '';

    const edgeRes = await fetch(`${SUPABASE_URL}/functions/v1/extract-document`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'apikey': SUPABASE_ANON_KEY,
      },
      body: fallbackFormData,
    });

    if (edgeRes.ok) {
      const result = await edgeRes.json();
      if (result?.extractedText && result.extractedText !== '[No se pudo extraer el texto]') {
        return result.extractedText;
      }
    } else {
      console.error('[fileUpload] Edge function error:', edgeRes.status);
    }
  } catch (fallbackError) {
    console.error('[fileUpload] Edge function fallback also failed:', fallbackError);
  }

  return '[Error al extraer el texto del documento]';
}

export async function uploadChatFile(
  file: File,
  userId: string,
  chatId: string,
  preExtractedText?: string,
  anonymize: boolean = false
): Promise<UploadedFile> {
  const fileName = `${userId}/${chatId}/${Date.now()}_${file.name}`;

  // 1. Upload to Supabase Storage
  const { data, error } = await supabase.storage
    .from('chat_uploads')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) throw error;

  // 2. Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from('chat_uploads')
    .getPublicUrl(fileName);

  // 3. Obtain Extracted Text (always from VPS API instead of local)
  let extractedText = preExtractedText || '';
  if (!extractedText) {
    extractedText = await extractTextViaVPS(file, anonymize);
  }

  // Truncate long text to prevent exceeding context
  if (extractedText && extractedText.length > 0) {
    extractedText = truncateExtractedText(extractedText);
  }

  return {
    name: file.name,
    size: file.size,
    type: file.type,
    url: publicUrl,
    path: data.path,
    extractedText,
  };
}

/**
 * Upload a file to AnythingLLM for RAG processing (large files).
 * Tries VPS endpoint first, falls back to local extraction.
 */
export async function uploadToAnythingLLM(
  file: File,
  workspace: string = 'general',
  anonymize: boolean = false
): Promise<{ location: string; workspace: string; extractedText: string }> {
  const API_BASE = API_URL;

  try {
    const token = await getValidAccessToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('workspace', workspace);
    if (anonymize) formData.append('anonymize', 'true');

    const res = await fetch(`${API_BASE}/upload-document`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });

    if (res.ok) {
      const data = await res.json();
      return {
        location: data.location || '',
        workspace: data.workspace || workspace,
        extractedText: data.extractedText || '[Documento procesado remotamente]',
      };
    }
    console.warn(`[uploadToAnythingLLM] VPS error ${res.status}, extracting locally...`);
  } catch (err) {
    console.warn('[uploadToAnythingLLM] VPS unreachable, extracting locally...', err);
  }

  // Fallback: extract text via the normal pipeline
  const extractedText = await extractTextViaVPS(file, anonymize);
  return {
    location: '',
    workspace,
    extractedText,
  };
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
